import { buildPrizeTiers, type Play } from '@lottocom/high-frequency-prize-tiers'
import { calculateHighFrequencyPrices } from '@lottocom/price-calculation'
import { resetCacheOnStaleVersion } from '~~/shared/utils/staleVersionCache'

import { COOKIE_HIGH_FREQUENCY_DISCLAIMER_ACCEPTED } from '~/constants/cookies'

import { useHighFrequencyStorePlaySlipMutation } from '../../../../../core/api/app/composables/backend.generated'
import {
  HighFrequency_GameConfigStatus,
  type HighFrequency_PlaySlipStoredError_MaxPlaySlipPriceExceeded,
  type HighFrequency_PlaySlipStoredError_PlayerRestricted,
  type Money,
  type Restrictions,
} from '../../../../../core/api/shared/@types/generated/backend/graphql-schema-types'
import { useProductStore } from '../../../app/stores/product'
import { useHighFrequencyGameStore } from '../stores/highFrequencyGame'
import { disabledAddOns as evaluateDisabledAddOns } from '../utils/highFrequencyGameAddOnEligibility'

export const HIGH_FREQUENCY_GAME_QUERY_FETCH_QUERY_KEY = 'high-frequency-game-query-fetch-policy'

export type StorePlaySlipResult =
  | { type: 'stored' }
  | { type: 'invalid-slip-reset' }
  | { type: 'ticket-limit-exceeded'; limit: Money }
  | { type: 'player-restricted'; balance: Money; grossTotal?: Money; restrictions: Restrictions }
  | { type: 'error' }

export const useHighFrequencyGame = (id?: string) => {
  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const { getHighFrequencyGameIdInCorrectCase } = useHighFrequencyGamesOfLicensedTerritory()
  const route = useRoute()
  const { params, query } = route

  const { log } = useDatadog()

  const gameName = id ?? String(params.gameId || query?.p)
  const gameId = getHighFrequencyGameIdInCorrectCase(gameName)

  if (!gameId)
    throw new Error(
      'useHighFrequencyGame composable can only be used in valid high frequency game routes',
    )

  const loggedIn = useAuth().isLoggedIn
  const store = useHighFrequencyGameStore(gameId)
  const {
    reset,
    setAddOn,
    setConfigVersion,
    setIsQuickPick,
    setNumbers: setSelectedNumbers,
    setQuantity,
    setWager,
  } = store
  const { isQuickPick, selectedAddOns, selectedNumbers, selectedQuantity, selectedWager } =
    storeToRefs(store)

  const { mutate } = useHighFrequencyStorePlaySlipMutation()
  const { setProduct } = useProductStore()
  const { addToast } = useToaster()

  const disclaimerAcceptedError = useState(() => false)

  const disclaimerAcceptedCookie = useCookie(COOKIE_HIGH_FREQUENCY_DISCLAIMER_ACCEPTED, {
    default: () => ({
      purchasedFinished: false,
      unchecked: false,
      value: false,
    }),
  })

  const fetchPolicyState = useState(HIGH_FREQUENCY_GAME_QUERY_FETCH_QUERY_KEY, () => 'cache-first')
  // only the game page refetches; checkout and checkout-redirect reuse the cached config
  const fetchPolicy = params.gameId ? fetchPolicyState : ref('cache-first')

  const {
    onError,
    onResult,
    refetch: refetchConfig,
    result: configResult,
  } = useHighFrequencyGameConfigQuery(
    {
      gameId,
      licensedTerritory,
    },
    {
      fetchPolicy,
      nextFetchPolicy: 'cache-first',
    },
  )

  onResult((result) => {
    // emitted before the network response on a cold cache (e.g. replay straight into checkout)
    if (!result.data) return

    const data = result.data.highFrequencyGameConfig

    if (isGraphqlSuccess(data)) {
      if (data.status === HighFrequency_GameConfigStatus.Inactive) {
        return navigateTo('/', { redirectCode: 302 })
      }

      return
    }

    throw create404Error()
  })

  onError((error) => {
    log('error', {
      component: `keno/${gameId}/index.vue`,
      error,
      message: 'Failed to fetch high frequency game config',
    })
    throw createError({ statusCode: 500 })
  })

  const config = computed(() => {
    const data = configResult.value?.highFrequencyGameConfig
    return isGraphqlSuccess(data) ? data : null
  })

  const hasSelectedNumbers = computed(() =>
    selectedNumbers.value.some(({ numbers }) => numbers.length > 0),
  )

  const resetSlipOnStaleConfig = (currentConfig: NonNullable<typeof config.value>) =>
    resetCacheOnStaleVersion({
      cache: 'slip',
      cachedVersion: store.configVersion,
      freshVersion: currentConfig.version,
      hasSelection: () => hasSelectedNumbers.value,
      log: (type, payload) => log(type, payload),
      productId: gameId,
      reset,
      syncVersion: setConfigVersion,
    })

  watch(
    config,
    (currentConfig) => {
      if (!currentConfig || import.meta.server) return

      if (nuxtApp.isHydrating) {
        onNuxtReady(() => resetSlipOnStaleConfig(currentConfig))
        return
      }

      resetSlipOnStaleConfig(currentConfig)
    },
    { immediate: true },
  )

  const setNumbers = (fieldName: string, numbers: number[], isQuickPick = false) => {
    setSelectedNumbers({
      name: fieldName,
      numbers,
    })
    setIsQuickPick(isQuickPick)
  }

  const addOns = computed(() => config.value?.addOns.map((addOn) => addOn.name) ?? [])

  const disabledAddOns = computed(() =>
    evaluateDisabledAddOns(
      config.value?.addOns ?? [],
      selectedNumbers.value.flatMap((field) => field.numbers),
    ),
  )

  watch(disabledAddOns, (disabled) => {
    disabled.forEach((name) => setAddOn(name, false))
  })

  const disclaimerAcceptedConfig = computed(
    () => (loggedIn.value && config.value?.disclaimerAccepted) ?? false,
  )

  const isDisclaimerAccepted = computed(
    () => disclaimerAcceptedConfig.value || disclaimerAcceptedCookie.value?.value,
  )

  const numberOfSpots = computed(() => selectedNumbers.value?.[0]?.numbers.length ?? 0)

  const remaining = computed(() => {
    const selectableMax = config.value?.numbers?.[0]?.selectableMax ?? 0

    return selectableMax - numberOfSpots.value
  })

  const jackpot = computed(() => config.value?.jackpot)

  const visibleJackpot = computed(() => {
    if (prizeTier.value) return prizeTier.value.maximumPrize
    return jackpot.value?.value
  })

  const wagers = computed(() => config.value?.wagers || [])

  const prizeTier = computed(() => {
    const currentConfig = config.value
    const currentAddOns = addOns.value
    const currentWagers = wagers.value
    const currentSelectedNumbers = selectedNumbers.value
    const wagerName = selectedWager.value

    if (!currentSelectedNumbers.length || !currentWagers.length || !wagerName || !currentConfig) {
      return null
    }

    const preparedAddOns = currentAddOns.map((name) => ({
      name,
      value: selectedAddOns.value.includes(name) ? 'true' : 'false',
    }))

    const preparedPlays = selectedNumbers.value
      .map((field) => ({
        fields: [{ name: field.name, numbers: field.numbers }],
        wager: {
          name: wagerName,
          value: currentWagers.find((w) => w.name === wagerName)?.price?.amount,
        },
      }))
      .filter(({ wager }) => wager.value !== undefined) as Play[]

    try {
      return buildPrizeTiers(
        {
          addOns: preparedAddOns,
          gameId,
          plays: preparedPlays,
        },
        config.value,
      )
    } catch {
      return null
    }
  })

  const deleteInvalidPlaySlipCache = async (): Promise<StorePlaySlipResult> => {
    log('warn', {
      component: `keno/${gameId}/index.vue`,
      message: 'Invalid play slip cache deleted',
      messageContext: {
        cachedVersion: store.configVersion,
        gameId,
        selectedAddOns: selectedAddOns.value,
        selectedNumbers: selectedNumbers.value,
        selectedQuantity: selectedQuantity.value,
        selectedWager: selectedWager.value,
      },
    })

    try {
      await refetchConfig()
    } catch (error) {
      log('error', {
        component: `keno/${gameId}/index.vue`,
        error,
        message: 'Failed to refetch high frequency game config after an invalid play slip',
      })
    }

    reset()

    addToast(
      {
        headline: t('lottery.gameslipnavbar.dialog.invalidplayslip.headline'),
        message: t('lottery.gameslipnavbar.dialog.invalidplayslip.reset.message'),
        tracking: {
          topicKey: 'hfg-store-error-invalid-play-slip',
        },
        type: 'danger',
      },
      'hfg-store-error-invalid-play-slip',
    )

    return { type: 'invalid-slip-reset' }
  }

  const storeHighFrequencyGameSlip = async (): Promise<StorePlaySlipResult> => {
    if (!config.value) return { type: 'error' }
    if (!playSlipValid.value) return { type: 'error' }

    disclaimerAcceptedError.value = false

    try {
      const mutation = await mutate({
        input: {
          addOns: selectedAddOns.value.map((addOn) => ({
            name: addOn,
            value: 'true',
          })),
          configVersion: config.value.version,
          disclaimerAccepted: isDisclaimerAccepted.value,
          gameId: config.value.gameId,
          licensedTerritory,
          numberOfDraws: selectedQuantity.value ?? 0,
          selectedNumbers: selectedNumbers.value.map((field) => ({
            name: field.name,
            selected: field.numbers,
          })),
          wagerName: selectedWager.value ?? '',
        },
      })

      const playSlipResult = mutation?.data?.highFrequencyStorePlaySlip

      const productId =
        playSlipResult && playSlipResult.__typename === 'HighFrequency_PlaySlipStored'
          ? playSlipResult.id
          : undefined

      if (isGraphqlSuccess(playSlipResult)) {
        if (productId) {
          setProduct(productId, gameId.toLowerCase(), ProductType.HighFrequencyGame)

          return { type: 'stored' }
        }
      } else {
        const errorObj = playSlipResult?.errors?.[0]

        if (
          hasGraphqlError(
            playSlipResult?.errors ?? [],
            'HighFrequency_PlaySlipStoredError_DisclaimerNotAccepted',
          )
        ) {
          // TODO Tracking
          disclaimerAcceptedError.value = true
        } else if (
          hasGraphqlError(
            playSlipResult?.errors ?? [],
            'HighFrequency_PlaySlipStoredError_PlayerRestricted',
          )
        ) {
          const err = errorObj as HighFrequency_PlaySlipStoredError_PlayerRestricted

          return {
            type: 'player-restricted',
            balance: err.accountBalance,
            grossTotal: err.grossTotal,
            restrictions: err.restrictions,
          }
        } else if (
          hasGraphqlError(
            playSlipResult?.errors ?? [],
            'HighFrequency_PlaySlipStoredError_InvalidPlaySlip',
          )
        ) {
          return await deleteInvalidPlaySlipCache()
        } else if (
          hasGraphqlError(
            playSlipResult?.errors ?? [],
            'HighFrequency_PlaySlipStoredError_PlayerCannotPlay',
          )
        ) {
          addToast(
            {
              headline: t('error.general.headline'),
              message: t('error.general.message'),
              tracking: {
                topicKey: 'hfg-store-error-player-cannot-play', // TODO tracking
              },
              type: 'danger',
            },
            'hfg-store-error-player-cannot-play',
          )
        } else if (
          hasGraphqlError(
            playSlipResult?.errors ?? [],
            'HighFrequency_PlaySlipStoredError_MaxPlaySlipPriceExceeded',
          )
        ) {
          const err = errorObj as HighFrequency_PlaySlipStoredError_MaxPlaySlipPriceExceeded

          return { type: 'ticket-limit-exceeded', limit: err.maxPlaySlipPrice }
        } else if (
          hasGraphqlError(
            playSlipResult?.errors ?? [],
            'HighFrequency_PlaySlipStoredError_UnexpectedError',
          )
        ) {
          addToast(
            {
              headline: t('error.general.headline'),
              message: t('error.general.message'),
              tracking: {
                topicKey: 'hfg-store-error-unexpected-error', // TODO tracking
              },
              type: 'danger',
            },
            'hfg-store-error-unexpected-error',
          )
        } else {
          addToast(
            {
              headline: t('error.general.headline'),
              message: t('error.general.message'),
              tracking: {
                topicKey: 'hfg-store-error-general-error', // TODO tracking
              },
              type: 'danger',
            },
            'hfg-store-error-general-error',
          )
        }
      }

      return { type: 'error' }
    } catch (error) {
      log('error', {
        component: `keno/${gameId}/index.vue`,
        error,
        message: 'Failed to store high frequency game',
      })

      return { type: 'error' }
    }
  }

  const currentPrice = computed(() => {
    if (
      !config.value ||
      !selectedWager.value ||
      !selectedNumbers.value ||
      !selectedNumbers.value.some(({ numbers }) => numbers.length > 0)
    )
      return null

    const configWager = config.value.wagers.find((wager) => wager.name === selectedWager.value)

    if (!configWager) return null

    return calculateHighFrequencyPrices(
      {
        addOns: selectedAddOns.value.map((name) => ({ name, value: 'true' })),
        configVersion: config.value.version,
        gameId,
        numberOfDraws: selectedQuantity.value ?? 0,
        plays: [
          {
            fields: selectedNumbers.value,
            wager: {
              name: configWager.name,
              value: configWager.price.amount,
            },
          },
        ],
      },
      config.value,
    )
  })

  const playSlipValid = computed(() => numberOfSpots.value > 0)

  const handleReset = () => {
    reset()
  }

  return {
    addOns,
    config,
    currentPrice,
    disabledAddOns,
    disclaimerAcceptedConfig,
    disclaimerAcceptedCookie,
    disclaimerAcceptedError,
    gameId,
    isQuickPick,
    jackpot,
    numberOfSpots,
    playSlipValid,
    prizeTier,
    remaining,
    reset: handleReset,
    selectedAddOns,
    selectedNumbers,
    selectedQuantity,
    selectedWager,
    setAddOn,
    setNumbers,
    setQuantity,
    setWager,
    storeHighFrequencyGameSlip,
    visibleJackpot,
    wagers,
  }
}
